import { Injectable } from '@angular/core';
import { HistoryNavigationRepositoryService } from './history-navigation-repository.service';
import { TranslateService } from '@ngx-translate/core';

export interface QuestionAnswerNext {
  branchFrom: string | null;
  questionTo: string | number;
  branchTo: string | number | null;
}

export interface QuestionAnswer {
  id: string | number | null;
  order?: number;
  imageId?: string | number;
  label?: Record<string, string>[];
  questionsNext?: QuestionAnswerNext[];
  [key: string]: unknown;
}

export class Question {
  id: number;
  title: [JSON];
  order;
  subtype;
  isOptionnal: Boolean;
  type: String;
  style?: String;
  questionSetId?: string;
  typeInformation: {
    answers?: QuestionAnswer[];
    isMultiAnswersAllowed?: boolean;
    qcmType?: number;
  };
  branch = Array();
  hiddenQuestion?: {
    activated: boolean;
    name: string;
  };
}

export class RandomQuestionSet {
  id: string;
  questionIds: string[];
}

export class Culture {
  culture: string;
  title: string;
  subtitle: string;
  endText: string;
  endLinkLabel?: string;
  startLabel: string;
  imageFlagPath: string;
}

/** Lien sortant de l'écran de fin (QR code sur borne, bouton en web). */
export class SurveyEndLink {
  url: string;
}

export class Survey {
  id: number;
  timestamp: number;
  expirationDate: number;
  imageId: number;
  imageUrl: string;
  imagePath: string;
  imageWebUrl: string;
  firstQuestion: number;
  defaultCulture: string;
  uniqueResponse: Boolean;
  cultures: [Culture];
  questions: [Question];
  randomQuestionSets?: RandomQuestionSet[];
  randomQuestions?: Question[];
  displayMono: boolean;
  endLink?: SurveyEndLink | null;
  open: {
    opening_days: [number];
    opening_hours: {
      start: string;
      end: string;
    };
  };
}

@Injectable({
  providedIn: 'root',
})
export class SurveyRepositoryService {
  hasUpdateSurvey: Boolean = false;
  survey: Survey = this.getSurveyObject();
  currentQuestionId;
  selectedCulture;
  unusedSurveyTimer;
  startSurveyTimer;
  /** Tirage en cours pour un slot aléatoire A : slotId → question B affichée. */
  private randomDrawBySlotId: Record<string, Question> = {};

  constructor(
    private translate: TranslateService,
    private historyNavigationRepository: HistoryNavigationRepositoryService
  ) {}

  getHasUpdateSurvey = function () {
    return this.hasUpdateSurvey;
  };

  setHasUpdateSurvey = function (value) {
    this.hasUpdateSurvey = value;
  };

  getSurvey() {
    return this.survey;
  }

  setSurvey(survey) {
    this.survey = survey;
    this.saveSurveyObject(survey);
  }

  getImagePath() {
    return this.survey.imagePath;
  }

  getUnusedSurveyTimer = function () {
    return this.unusedSurveyTimer;
  };

  setUnusedSurveyTimer = function (value) {
    this.unusedSurveyTimer = value;
  };

  getStartSurveyTimer = function () {
    return this.startSurveyTimer;
  };

  setStartSurveyTimer = function (value) {
    this.startSurveyTimer = value;
  };

  getLocalizedText = function (titles, culture) {
    var text = titles.find(function (title) {
      return title[culture] != null;
    });
    if (text != undefined) {
      return text[culture];
    } else {
      return '';
    }
  };

  setFieldEmpty = function (value) {
    this.answerField = value;
    //broadcastEmptyField(value); // observe "value" et regarde si elle change
  };

  deleteSurvey() {
    localStorage.removeItem('survey');
    this.survey = undefined;
    this.clearRandomDraws();
  }

  getCurrentSurveyId() {
    if (this.survey && this.survey != null && this.survey.id) {
      return this.survey.id;
    } else {
      return 0;
    }
  }

  getSurveyObject() {
    var surveyString = localStorage.getItem('survey');
    return surveyString ? JSON.parse(surveyString) : {};
  }

  saveSurveyObject = function (currentSurvey) {
    var cache = [];
    const serializedSurvey = JSON.stringify(
      currentSurvey,
      function (key, value) {
        if (key === 'image_content' || key === 'toPrint') return;
        if (typeof value === 'string' && value.startsWith('data:')) return;
        if (typeof value === 'object' && value !== null) {
          if (cache.indexOf(value) !== -1) return;
          cache.push(value);
        }
        return value;
      }
    );
    localStorage.setItem('survey', serializedSurvey);
  };

  getCurrentQuestion(questionId) {
    if (this.randomDrawBySlotId[questionId]) {
      return this.randomDrawBySlotId[questionId];
    }
    return this.getSurvey().questions.find(function (obj) {
      return obj.id === questionId;
    });
  }

  /** Id à utiliser pour enregistrer la réponse (question B tirée, pas le slot A). */
  getAnswerQuestionId(): string | number {
    const currentId = this.currentQuestionId;
    if (this.randomDrawBySlotId[currentId]) {
      return this.randomDrawBySlotId[currentId].id;
    }
    const slotQuestion = this.getSurvey().questions?.find(
      (q) => q.id === currentId
    );
    if (slotQuestion && this.isRandomSlotQuestion(slotQuestion)) {
      const resolved = this.beginRandomQuestionVisit(currentId);
      if (resolved) {
        return resolved.id;
      }
    }
    return currentId;
  }

  isRandomSlotQuestion(question: Question | undefined): boolean {
    return question?.type === 'Random';
  }

  /**
   * Nouveau tirage à chaque affichage du slot aléatoire dans le parcours.
   */
  beginRandomQuestionVisit(slotQuestionId: string | number): Question | null {
    const slotQuestion = this.getSurvey().questions.find(
      (q) => q.id === slotQuestionId
    );
    if (!slotQuestion || !this.isRandomSlotQuestion(slotQuestion)) {
      return null;
    }

    delete this.randomDrawBySlotId[slotQuestionId];

    const questionSetId = slotQuestion.questionSetId;
    const questionSet = (this.survey.randomQuestionSets ?? []).find(
      (set) => String(set.id) === String(questionSetId)
    );
    if (!questionSet?.questionIds?.length) {
      return null;
    }

    const poolQuestions = this.survey.randomQuestions ?? [];
    const candidates = questionSet.questionIds
      .map((poolId) =>
        poolQuestions.find(
          (poolQuestion) => String(poolQuestion.id) === String(poolId)
        )
      )
      .filter(Boolean) as Question[];

    if (!candidates.length) {
      return null;
    }

    const picked = candidates[Math.floor(Math.random() * candidates.length)];
    const resolved = this.mergeSlotNavigationIntoPoolQuestion(
      JSON.parse(JSON.stringify(picked)),
      slotQuestion
    );
    this.randomDrawBySlotId[slotQuestionId] = resolved;
    return resolved;
  }

  private mergeSlotNavigationIntoPoolQuestion(
    poolQuestion: Question,
    slotQuestion: Question
  ): Question {
    const slotAnswers: QuestionAnswer[] =
      slotQuestion.typeInformation?.answers ?? [];
    const navigationTemplate: QuestionAnswerNext[] =
      slotAnswers.find((answer) => answer.questionsNext?.length)
        ?.questionsNext ??
      slotAnswers[0]?.questionsNext ??
      [];

    poolQuestion.branch = slotQuestion.branch;

    const poolAnswers = poolQuestion.typeInformation?.answers;
    if (poolAnswers?.length) {
      poolQuestion.typeInformation.answers = poolAnswers.map((answer) => ({
        ...answer,
        questionsNext: JSON.parse(
          JSON.stringify(navigationTemplate)
        ) as QuestionAnswerNext[],
      }));
    }

    return poolQuestion;
  }

  clearRandomDraws() {
    this.randomDrawBySlotId = {};
  }

  getFirstQuestion() {
    return this.survey.firstQuestion;
  }

  getCurrentQuestionId() {
    return this.currentQuestionId;
  }

  setCurrentQuestionId(value) {
    this.currentQuestionId = value;
  }

  resetSurvey = function () {
    this.currentQuestionId = this.getFirstQuestion();
    this.selectedCulture = null;
    this.clearRandomDraws();
  };

  getSelectedCulture() {
    if (this.selectedCulture == null) {
      return this.survey.defaultCulture;
    }
    return this.selectedCulture;
  }

  setSelectedCulture(value) {
    this.translate.use(value);
    this.survey.defaultCulture = value;
  }

  getCurrentTitle = function () {
    return this.survey.title;
  };

  getNextQuestion(answers, currentAnswerId, currentBranch) {
    var result;
    var currentAnswer = answers.find((el) => el.id == currentAnswerId);

    if (!currentAnswer) return null;

    var next = currentAnswer.questionsNext;

    if (!next) return null;
    return next.find((el) => el.branchFrom == currentBranch);
  }

  manageNavigationNextQuestion = function (
    answers,
    currentAnswerId,
    currentBranch
  ) {
    const currentQuestion = this.survey.questions.find(
      (question) => question.id === this.currentQuestionId
    );
    if (!currentQuestion) return;

    // Vérifier si la question a une branche
    const branch =
      currentQuestion.branch && currentQuestion.branch.length > 0
        ? currentQuestion.branch[0]
        : null;

    var nextQuestion = null;

    // Si la question a une branche, utiliser la logique normale
    if (branch !== null) {
      nextQuestion = this.getNextQuestion(answers, currentAnswerId, branch);
    } else {
      // Pour les entry questions sans branche, chercher une réponse par défaut
      // ou la première réponse disponible
      if (answers && answers.length > 0) {
        const defaultAnswer =
          answers.find((el) => el.id == currentAnswerId) || answers[0];
        if (
          defaultAnswer &&
          defaultAnswer.questionsNext &&
          defaultAnswer.questionsNext.length > 0
        ) {
          // Prendre la première question suivante disponible
          nextQuestion = defaultAnswer.questionsNext[0];
        }
      }
    }

    if (!nextQuestion) return;
    this.historyNavigationRepository.pushHistory(
      nextQuestion.branchTo,
      nextQuestion.questionTo
    );
    this.setCurrentQuestionId(nextQuestion.questionTo);
  };

  manageNavigationPreviousQuestion = function () {
    this.historyNavigationRepository.popHistory();
    var previousQuestion = this.historyNavigationRepository.getLastQuestionId();
    this.setCurrentQuestionId(previousQuestion);
  };

  isDisplayMonoPage() {
    return this.survey.displayMono;
  }
}
